import React, { useEffect, useMemo, useState } from 'react'
import {
	Controller,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form'
import Select, { MultiValue } from 'react-select'

import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import Flex from '@components/Flex'
import ToggleButton from '@components/ToggleButton'
import { PlusIcon, TrashIcon } from '@heroicons/react/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import {
	AddTutorialRequest,
	TutorialRule,
	TutorialRuleOperator,
	TutorialVisibility,
} from '@models/tutorial.model'
import { tutorialSchema } from '@schemas/tutorial.schema'
import { classesApi } from '@services/ClassesApi'
import { inputIsValid } from '@utils/helpers'
import {
	resolveAudience,
	ruleOperatorLabels,
} from '@utils/helpers/tutorialAudience'

import { extractYoutubeVideoId } from '../youtube'

type ClassOption = { value: number; label: string }

export type TutorialFormValues = {
	title: string
	description: string
	youtubeUrl: string
	visibility: TutorialVisibility
	classIds: MultiValue<ClassOption>
	rules: TutorialRule[]
}

export type TutorialFormDefaults = {
	title: string
	description: string
	youtubeUrl: string
	isPublished: boolean
	visibility: TutorialVisibility
	classIds: ClassOption[]
	rules: TutorialRule[]
}

const emptyDefaults: TutorialFormDefaults = {
	title: '',
	description: '',
	youtubeUrl: '',
	isPublished: true,
	visibility: 'restricted',
	classIds: [],
	rules: [],
}

const operators: TutorialRuleOperator[] = [
	'contains',
	'starts_with',
	'ends_with',
]

const styles = {
	section: 'mt-6',
	sectionTitle: 'text-sm font-medium text-gray-700',
	sectionHint: 'text-sm text-gray mt-1',
	textarea:
		'shadow-3 block w-full sm:text-sm border-gray-300 rounded-md placeholder:text-gray px-[13px] py-[6.5px]',
	radioGroup: 'flex gap-6 mt-2',
	radioLabel: 'flex items-center gap-2 text-sm text-gray-700',
	ruleRow: 'flex gap-2 items-start mt-2',
	ruleSelect:
		'shadow-3 sm:text-sm border-gray-300 rounded-md px-[13px] py-[6.5px] w-[160px]',
	reachBox: 'mt-4 rounded-md bg-gray-100 px-4 py-3 text-sm text-gray-700',
	reachList: 'mt-2 max-h-[140px] overflow-y-auto text-xs text-gray',
	preview: 'mt-2 rounded-md overflow-hidden bg-black aspect-video w-full',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
	error: 'text-red text-xs mt-1',
}

interface TutorialFormProps {
	defaultValues?: TutorialFormDefaults
	isLoading: boolean
	submitLabel: string
	onSubmit: (payload: AddTutorialRequest) => void
	onCancel: () => void
}

export default function TutorialForm({
	defaultValues = emptyDefaults,
	isLoading,
	submitLabel,
	onSubmit,
	onCancel,
}: TutorialFormProps) {
	const [isPublished, setIsPublished] = useState(defaultValues.isPublished)

	const { data: dataClasses } = classesApi.useListQuery({ limit: 10000 })

	const {
		register,
		control,
		handleSubmit,
		watch,
		reset,
		formState: { errors, touchedFields },
	} = useForm<TutorialFormValues>({
		mode: 'onBlur',
		resolver: yupResolver(tutorialSchema),
		defaultValues,
	})

	const { fields, append, remove } = useFieldArray({ control, name: 'rules' })

	useEffect(() => {
		reset(defaultValues)
		setIsPublished(defaultValues.isPublished)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(defaultValues), reset])

	const classOptions: ClassOption[] = useMemo(
		() =>
			dataClasses?.data?.map((current) => ({
				value: current.id,
				label: current.name,
			})) || [],
		[dataClasses]
	)

	const visibility = watch('visibility')
	const youtubeUrl = watch('youtubeUrl')
	const selectedClasses = watch('classIds')
	const watchedRules = watch('rules')

	const videoId = extractYoutubeVideoId(youtubeUrl || '')

	const audience = useMemo(
		() =>
			resolveAudience({
				classes: classOptions.map((option) => ({
					id: option.value,
					name: option.label,
				})),
				classIds: (selectedClasses || []).map((option) => option.value),
				rules: (watchedRules || []).filter((rule) => rule.value?.trim()),
			}),
		[classOptions, selectedClasses, watchedRules]
	)

	const submit: SubmitHandler<TutorialFormValues> = (data) => {
		const isRestricted = data.visibility === 'restricted'

		onSubmit({
			title: data.title,
			description: data.description || null,
			youtubeUrl: data.youtubeUrl,
			isPublished,
			visibility: data.visibility,
			classIds: isRestricted
				? (data.classIds || []).map((option) => option.value)
				: [],
			rules: isRestricted
				? (data.rules || []).filter((rule) => rule.value?.trim())
				: [],
		})
	}

	return (
		<form onSubmit={handleSubmit(submit)}>
			<InputGroup className='mt-4' label='Título' id='title'>
				<Input
					type='text'
					isValid={inputIsValid(errors.title, touchedFields.title)}
					{...register('title')}
				/>
			</InputGroup>

			<InputGroup
				className={styles.section}
				label='Descrição'
				id='description'
				hint='Uma linha explicando o que o aluno vai ver. Aparece embaixo do título na plataforma.'
			>
				<textarea
					className={styles.textarea}
					rows={2}
					{...register('description')}
				/>
			</InputGroup>

			<InputGroup
				className={styles.section}
				label='Link do vídeo no YouTube'
				id='youtubeUrl'
				hint='Cole a URL do vídeo. Vale watch, youtu.be, embed ou shorts.'
			>
				<Input
					type='text'
					isValid={inputIsValid(errors.youtubeUrl, touchedFields.youtubeUrl)}
					{...register('youtubeUrl')}
				/>
			</InputGroup>

			{youtubeUrl && !videoId && (
				<p className={styles.error}>
					Não reconheci esse link do YouTube. Confira antes de salvar.
				</p>
			)}

			{videoId && (
				<div className={styles.preview}>
					<iframe
						title='Prévia do tutorial'
						width='100%'
						height='100%'
						src={`https://www.youtube.com/embed/${videoId}`}
						frameBorder='0'
						allowFullScreen
					/>
				</div>
			)}

			<div className={styles.section}>
				<ToggleButton
					id='isPublished'
					enableText='Publicado'
					disableText='Rascunho'
					isEnabled={defaultValues.isPublished}
					onChange={setIsPublished}
				/>
			</div>

			<div className={styles.section}>
				<p className={styles.sectionTitle}>Quem vê este tutorial</p>
				<div className={styles.radioGroup}>
					<label className={styles.radioLabel} htmlFor='visibility-all'>
						<input
							id='visibility-all'
							type='radio'
							value='all'
							{...register('visibility')}
						/>
						Todos os alunos
					</label>
					<label className={styles.radioLabel} htmlFor='visibility-restricted'>
						<input
							id='visibility-restricted'
							type='radio'
							value='restricted'
							{...register('visibility')}
						/>
						Turmas específicas e/ou regras
					</label>
				</div>
			</div>

			{visibility === 'restricted' && (
				<>
					<InputGroup
						className={styles.section}
						label='Turmas'
						id='classIds'
						hint='Vínculo direto, do jeito que os materiais fazem.'
					>
						<Controller
							name='classIds'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Select
									isMulti
									options={classOptions}
									value={value}
									onChange={onChange}
									placeholder='Selecione as turmas'
									noOptionsMessage={() => 'Nenhuma turma encontrada'}
								/>
							)}
						/>
					</InputGroup>

					<div className={styles.section}>
						<p className={styles.sectionTitle}>Regras por nome da turma</p>
						<p className={styles.sectionHint}>
							Alcança qualquer turma cujo nome bater com a regra, inclusive as
							que ainda vão ser criadas. É o que resolve os retas finais sem
							precisar voltar aqui a cada turma nova.
						</p>

						{fields.map((field, index) => (
							<div key={field.id} className={styles.ruleRow}>
								<select
									className={styles.ruleSelect}
									{...register(`rules.${index}.operator` as const)}
								>
									{operators.map((operator) => (
										<option key={operator} value={operator}>
											{ruleOperatorLabels[operator]}
										</option>
									))}
								</select>
								<div className='flex-1'>
									<Input
										type='text'
										placeholder='Reta Final'
										{...register(`rules.${index}.value` as const)}
									/>
									{errors.rules?.[index]?.value && (
										<p className={styles.error}>
											{errors.rules[index]?.value?.message}
										</p>
									)}
								</div>
								<Button
									type='button'
									variant='outline'
									className='!px-2 !py-2'
									leftIcon={<TrashIcon />}
									onClick={() => remove(index)}
								>
									<></>
								</Button>
							</div>
						))}

						<Button
							type='button'
							variant='outline'
							className='mt-3'
							leftIcon={<PlusIcon />}
							onClick={() => append({ operator: 'contains', value: '' })}
						>
							Adicionar regra
						</Button>
					</div>

					<div className={styles.reachBox}>
						<strong>
							Alcance: {audience.reached.length}{' '}
							{audience.reached.length === 1 ? 'turma' : 'turmas'}
						</strong>
						{audience.matchedByRuleOnly.length > 0 && (
							<span> ({audience.matchedByRuleOnly.length} por regra)</span>
						)}
						{audience.reached.length === 0 && (
							<p className={styles.sectionHint}>
								Sem turma e sem regra que case, nenhum aluno vai ver este
								tutorial.
							</p>
						)}
						{audience.reached.length > 0 && (
							<ul className={styles.reachList}>
								{audience.reached.map((current) => (
									<li key={current.id}>{current.name}</li>
								))}
							</ul>
						)}
					</div>
				</>
			)}

			<Flex className={styles.buttonGroup}>
				<Button type='button' variant='outline' onClick={onCancel}>
					Cancelar
				</Button>
				<Button type='submit' disabled={isLoading || !videoId}>
					{submitLabel}
				</Button>
			</Flex>
		</form>
	)
}
